import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  Box,
  Card,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  useThemedValueSwitch,
} from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';

import { useConsultation } from 'api';
import { ConsultationStatusIndicator } from 'components';
import { consulteeIdState } from 'store';
import { isRpc } from 'tools';

import { StandardDateFormats, useFormatLocaleDate } from 'config';

export type AssigneeDetailsProps = {
  topPadding?: boolean;
  showFooter?: boolean;
};

export const AssigneeDetails: React.FC<AssigneeDetailsProps> = ({
  topPadding = true,
  showFooter = true,
}) => {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const { data: consultation } = useConsultation(+(id ?? 0));
  const { t } = useTranslation('assignee_details');
  const { tvs } = useThemedValueSwitch();
  const consulteeId = useRecoilValue(consulteeIdState);
  const addressBackgroundColour = () => {
    return isRpc(consultation) ? COLOURS.blue[100] : COLOURS.gray[200];
  };
  const formatLocaleDate = useFormatLocaleDate();

  const getDaysRemaining = (deadline: Date | undefined): string | undefined => {
    if (!deadline) return undefined;

    const deadlineDate = new Date(deadline);
    if (!deadlineDate?.getTime) return undefined;

    const currentDateTime = new Date().getTime();
    const daysRemaining = Math.ceil(
      (deadlineDate.getTime() - currentDateTime) / (1000 * 3600 * 24)
    );
    return daysRemaining > 0
      ? ` (${daysRemaining} ${
          daysRemaining === 1
            ? t('day_remaining', 'day remaining')
            : t('days_remaining', 'days remaining')
        })`
      : undefined;
  };

  const showLastResponded = consultation?.isArchived && consultation?.lastRespondedDate;
  const lastUpdated = showLastResponded
    ? {
        date: consultation?.lastRespondedDate,
        consulteeId: consultation?.lastRespondedConsulteeId,
        consulteeName: consultation?.lastRespondedBy,
      }
    : {
        date: consultation?.lastVisitedDate,
        consulteeId: consultation?.lastVisitedConsulteeId,
        consulteeName: consultation?.lastVisitedBy,
      };

  const lastUpdatedDate = formatLocaleDate(lastUpdated.date, StandardDateFormats.dateAndTime);

  const lastUpdatedConsultee =
    lastUpdated.consulteeId === consulteeId
      ? `${lastUpdated.consulteeName} (${t('you_text', 'You')})`
      : lastUpdated.consulteeName;

  const mobileLastVisited = (
    <>
      <HStack mr='8px'>
        <Box>{lastUpdatedDate}</Box>
      </HStack>
      {lastUpdated.consulteeName && (
        <HStack>
          <FormLabel
            display='inline'
            mb={0}
            textTransform={{ base: 'capitalize', lg: 'unset' }}
            id='byText'
          >
            {t('by', 'By')}:
          </FormLabel>
          <Box>{lastUpdatedConsultee}</Box>
        </HStack>
      )}
    </>
  );

  const desktopLastVisited = (
    <>
      {lastUpdatedDate}
      {lastUpdated.consulteeName && (
        <>
          <Box fontWeight='bold' px={1} as='span'>
            {t('by', 'By').toLowerCase()}
          </Box>
          {lastUpdatedConsultee}
        </>
      )}
    </>
  );

  const gridTemplateColumnsStyle = tvs({
    tq: { base: '1fr', lg: 'auto 1fr' },
    ni: { base: '1fr', lg: '100px 1fr' },
  });

  return (
    <Card mt={topPadding ? 8 : 0} textAlign='left' data-testid='assignee-details'>
      <FormControl>
        <Grid
          templateColumns={{ base: '1fr', lg: '45% 55%' }}
          templateRows='1fr 1fr'
          gridAutoFlow={{ base: 'unset', lg: 'column' }}
          gridRowGap='12px'
          pos='relative'
          p={tvs({ tq: '5', ni: '4' })}
          sx={{
            div: {
              d: tvs({ tq: 'flex' }),
              gridGap: tvs({ tq: '.25rem' }),
            },
          }}
        >
          <ConsultationStatusIndicator
            position='absolute'
            right='4'
            top='4'
            consultation={consultation}
            screenReaderLabel={`${t('consultation_status', 'Consultation status')}: ${
              consultation?.status
            }`}
          />
          <Grid templateColumns={gridTemplateColumnsStyle}>
            <FormLabel id='assigneeLabel' mr={tvs({ tq: '1' })} mb={0}>
              {t('assignee_name', 'Assignee')}:
            </FormLabel>
            <span>{consultation?.assignee}</span>
          </Grid>

          <Grid templateColumns={gridTemplateColumnsStyle}>
            <FormLabel id='respondedLabel' mr={tvs({ tq: '1' })} mb={0}>
              {showLastResponded
                ? t('responded', 'Responded')
                : t('last_visited', 'Last visited')}
              :
            </FormLabel>
            <Box display='inline-flex' flexWrap='wrap'>
              {isMobile ? mobileLastVisited : desktopLastVisited}
            </Box>
          </Grid>

          <Grid mr={{ base: 0, lg: '104px' }} templateColumns={gridTemplateColumnsStyle}>
            <FormLabel id='requestedDate' mb={0} mr={tvs({ tq: '1' })}>
              {t('Date_requested', 'Requested')}:
            </FormLabel>
            <span>{formatLocaleDate(consultation?.requestedDate)}</span>
          </Grid>

          <Grid templateColumns={gridTemplateColumnsStyle}>
            <FormLabel id='deadlineDate' mb={0} mr={tvs({ tq: '1' })}>
              {t('deadline', 'Deadline')}:
            </FormLabel>
            <span>
              {formatLocaleDate(consultation?.deadlineDate)}
              {getDaysRemaining(consultation?.deadlineDate)}
            </span>
          </Grid>
        </Grid>

        {showFooter && (
          <Grid
            p={4}
            gridGap={{ base: '4', lg: '0' }}
            templateColumns={{
              base: '1fr',
              lg: '50% auto auto',
            }}
            backgroundColor={addressBackgroundColour()}
          >
            <GridItem>
              <FormLabel display={{ base: 'block', lg: 'inline' }} pr={1} mb={0}>
                {t('address', 'Address')}:
              </FormLabel>
              {consultation?.address}
            </GridItem>
            <GridItem>
              <FormLabel display='inline' pr={1} mb={0}>
                {t('reference', 'Reference')}:
              </FormLabel>
              {consultation?.reference}
            </GridItem>
            <GridItem textAlign={{ base: 'left', xl: 'right' }}>
              <FormLabel display='inline' pr={1} mb={0}>
                {t('consultation_type', 'Consultation type')}:
              </FormLabel>
              {consultation?.consultationType}
            </GridItem>
          </Grid>
        )}
      </FormControl>
    </Card>
  );
};
